/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-console */

import { atom } from 'jotai';
import { atomWithReset, atomWithStorage, useResetAtom } from 'jotai/utils';
import React from 'react';
import { companyIdAtom } from 'views/state';

export enum SourceType {
  ALL = '',
  WAS = 'was_data',
  SAST = 'sast',
  EP = 'endpoint_data',
  EP_EXTERNAL = 'endpoint_data__External',
  EP_INTERNAL = 'endpoint_data__Internal',
  EP_EC2 = 'endpoint_data__EC2',
  CA = 'ca_data',
  CA_SERVER = 'ca_data__Server',
  CA_WORKSTATIONS = 'ca_data__Workstations',
}

export const sourceTypeConfig = {
  [SourceType.SAST]: {
    filterLabel: 'Repositories',
    label: 'Repositories',
    badgeLabel: 'Repositories',
    iconClass: 'fas fa-code',
    icon: React.createElement('i', { className: 'fas fa-code' }),
    tooltip: 'Filter by specific repositories, search is supported.',
    onIgnoreVuln: () => {
      localStorage.setItem('sast_refresh', Math.random().toString());
    },
  },
  [SourceType.CA]: {
    filterLabel: 'Cloud Agents',
    label: 'Cloud Agents',
    badgeLabel: 'Cloud Agents',
    iconClass: 'fas fa-cloud',
    icon: React.createElement('i', { className: 'fas fa-cloud' }),
    tooltip: 'Filter by specific cloud agents, search is supported.',
    onIgnoreVuln: () => {
      localStorage.setItem('ca_refresh', Math.random().toString());
    },
  },
  [SourceType.CA_SERVER]: {
    filterLabel: 'Cloud Agents (Servers)',
    label: 'Cloud Agents - Servers',
    badgeLabel: 'Cloud Agents',
    iconClass: 'fas fa-server',
    icon: React.createElement('i', { className: 'fas fa-server' }),
    tooltip: 'Filter by specific cloud agents, search is supported.',
    onIgnoreVuln: () => {
      localStorage.setItem('ca_refresh', Math.random().toString());
    },
  },
  [SourceType.CA_WORKSTATIONS]: {
    filterLabel: 'Cloud Agents (Workstations)',
    label: 'Cloud Agents -  Workstations',
    badgeLabel: 'Cloud Agents',
    iconClass: 'fas fa-desktop',
    icon: React.createElement('i', { className: 'fas fa-desktop' }),
    tooltip: 'Filter by specific cloud agents, search is supported.',
    onIgnoreVuln: () => {
      localStorage.setItem('ca_refresh', Math.random().toString());
    },
  },
  [SourceType.WAS]: {
    filterLabel: 'Web Applications',
    label: 'Web Applications',
    badgeLabel: 'Web Applications',
    iconClass: 'fas fa-globe',
    icon: React.createElement('i', { className: 'fas fa-globe' }),
    tooltip: 'Filter by specific web applications, search is supported.',
    onIgnoreVuln: () => {
      localStorage.setItem('wa_refresh', Math.random().toString());
    },
  },
  [SourceType.EP]: {
    filterLabel: 'Endpoints',
    label: 'Endpoints',
    badgeLabel: 'Endpoints',
    iconClass: 'fas fa-desktop',
    icon: React.createElement('i', { className: 'fas fa-desktop' }),
    tooltip: 'Filter by specific endpoints, search is supported.',
    onIgnoreVuln: () => {
      localStorage.setItem('ep_refresh', Math.random().toString());
    },
  },
  [SourceType.EP_INTERNAL]: {
    filterLabel: 'Endpoints (Internal)',
    label: 'Endpoints - Internal',
    badgeLabel: 'Endpoints',
    iconClass: 'fas fa-network-wired',
    icon: React.createElement('i', { className: 'fas fa-network-wired' }),
    tooltip: 'Filter by specific endpoints, search is supported.',
    onIgnoreVuln: () => {
      localStorage.setItem('ep_refresh', Math.random().toString());
    },
  },
  [SourceType.EP_EXTERNAL]: {
    filterLabel: 'Endpoints (External)',
    label: 'Endpoints - External',
    badgeLabel: 'Endpoints',
    iconClass: 'fas fa-desktop',
    icon: React.createElement('i', { className: 'fas fa-desktop' }),
    tooltip: 'Filter by specific endpoints, search is supported.',
    onIgnoreVuln: () => {
      localStorage.setItem('ep_refresh', Math.random().toString());
    },
  },
  [SourceType.EP_EC2]: {
    filterLabel: 'Endpoints (AWS)',
    label: 'Endpoints - AWS',
    badgeLabel: 'Endpoints',
    iconClass: 'fab fa-aws',
    icon: React.createElement('i', { className: 'fab fa-aws' }),
    tooltip: 'Filter by specific endpoints, search is supported.',
    onIgnoreVuln: () => {
      localStorage.setItem('ep_refresh', Math.random().toString());
    },
  },
  default: {
    filterLabel: 'Hosts',
    label: 'Hosts',
    badgeLabel: 'Hosts',
    iconClass: 'fas fa-server',
    icon: React.createElement('i', { className: 'fas fa-server' }),
    tooltip: 'Filter by specific hosts, search is supported.',
    onIgnoreVuln: () => {
      // raise exception
      throw new Error('Invalid source type');
    },
  },
};

export const DEFAULT_COLUMN_KEYS_ALL = [
  // 'id',
  'name',
  'default_host',
  'status',
  'severity',
  'last_detected_date',
  'last_tested_date',
  'age',
  'due_date',
  'assigned_member_label',
  'actions',
];

export const DEFAULT_COLUMN_KEYS_CA = [
  'qid',
  'name',
  'ca_host',
  'status',
  'severity',
  'last_detected_date',
  'last_tested_date',
  'age',
  'due_date',
  'assigned_member_label',
  'actions',
];

export const DEFAULT_COLUMN_KEYS_EP = [
  // 'id',
  'qid',
  'name',
  'ep_host',
  'status',
  'severity',
  'last_detected_date',
  'last_tested_date',
  'age',
  'due_date',
  'assigned_member_label',
  'actions',
];

export const DEFAULT_COLUMN_KEYS_WAS = [
  // 'id',
  'qid',
  'name',
  'was_host',
  'status',
  'severity',
  'last_detected_date',
  'last_tested_date',
  'age',
  'due_date',
  'assigned_member_label',
  'actions',
];

export const DEFAULT_COLUMN_KEYS_SAST = [
  // 'id',
  'sonarqube_rule',
  'name',
  'sast_repo',
  'status',
  'severity',
  'last_detected_date',
  'last_tested_date',
  'age',
  'due_date',
  'assigned_member_label',
  'actions',
];

const DateFilterKeys = [
  'due_date',
  'created_at',
  'last_detected_date',
  'last_tested_date',
  'last_fixed_datetime',
  'last_reopened_datetime',
  'ignored_date',
  'unignored_date',
];

export const columnKeysAllAtom = atomWithStorage(
  'findings_listing_active_column_keys_all',
  DEFAULT_COLUMN_KEYS_ALL,
);

export const columnKeysCAAtom = atomWithStorage(
  'findings_listing_active_column_keys_ca',
  DEFAULT_COLUMN_KEYS_CA,
);

export const columnKeysEPAtom = atomWithStorage(
  'findings_listing_active_column_keys_ep',
  DEFAULT_COLUMN_KEYS_EP,
);

export const columnKeysWASAtom = atomWithStorage(
  'findings_listing_active_column_keys_was',
  DEFAULT_COLUMN_KEYS_WAS,
);

export const columnKeysSASTAtom = atomWithStorage(
  'findings_listing_active_column_keys_sast',
  DEFAULT_COLUMN_KEYS_SAST,
);

/**
 * NOTE: we should not consume this atom value directly in components,
 * instead use the derived atom `parsedFilterValuesAtom`
 * Atom to store filter values in localStorage, including the date values.
 * This atom stores the filter values as a string in localStorage.
 * so the date values will get stored as string.
 */
export const filterValuesAtom = atomWithStorage<Record<string, any>>(
  'findings_listing_filter_values_atom',
  {
    status: ['NEW,ACTIVE,REOPENED'],
  },
);

/**
 * Derived atom to parse the date string back into a Date object.
 * This atom reads the filter values from filterValuesAtom and parses the fromDate string
 * back into a Date object for use in components.
 */
export const parsedFilterValuesAtom = atom<Record<string, any>>((get) => {
  const filterValues = get(filterValuesAtom);
  const updatedFilterValues = {
    ...filterValues,
    ...DateFilterKeys.reduce((acc, key) => {
      if (filterValues[key]) {
        acc[key] = filterValues[key].map((dateRange) => ({
          from: dateRange.from ? new Date(dateRange.from) : null,
          to: dateRange.to ? new Date(dateRange.to) : null,
        }));
      }
      return acc;
    }, {}),
  };
  return updatedFilterValues;
});

export const selectedHostsAtom = atom((get) => {
  return get(filterValuesAtom)?.host_ids || [];
});

export const selectedRowIdsAtom = atomWithReset<number[]>([]);
export const forceRerenderFindingGridAtom = atom<string>('');
export const findingGridDataRefreshAtom = atomWithStorage<string>(
  'findings_refresh',
  '',
);

export const paginationConfigAtom = atomWithReset({
  page_size: 10, // Default page size
  page: 1,
});
export const orderbyAtom = atomWithReset<string>(
  '-severity,-last_detected_date,id',
);
export const sortOrderAtom = atomWithReset<'desc' | 'asc'>('desc');

export const sourceTypeAtom = atomWithStorage<string>(
  'findings_source_type',
  '',
);

export const agentKeyAtom = atomWithStorage<string>('agentKey', '');

/**
 * Atom to track the initialization state of query string filters for findings page (used for drill downs from dashboards etc.)
 *
 * This atom holds a boolean value indicating whether the query string filters
 * have been initialized. It is initially set to `false`.
 */
export const isQuerystringFiltersInitializedAtom = atom<boolean>(false);

export const findingsDataQueryParamsAtom = atom(async (get) => {
  const companyId = get(companyIdAtom);
  const paginationConfig = get(paginationConfigAtom);
  const orderBy = get(orderbyAtom);
  const order = get(sortOrderAtom);
  // Add id to orderBy if not present, then apply sort order
  const orderByWithId = orderBy
    .split(',')
    .some((field) => field.trim() === 'id' || field.trim() === '-id')
    ? orderBy
    : `${orderBy},id`;
  const ordering = orderByWithId
    .split(',')
    .map(
      (field) =>
        `${order === 'desc' && !field.startsWith('-') ? '-' : ''}${field}`,
    )
    .join(',');
  const filterValues = get(parsedFilterValuesAtom);
  const sourceType = get(sourceTypeAtom);
  const agentKey = get(agentKeyAtom);
  const _findingGridDataRefresh = get(findingGridDataRefreshAtom);

  if (companyId && companyId !== -1) {
    const params = {
      company_id: companyId.toString(),
      page: paginationConfig.page.toString(), // Specify the page number you want to retrieve
      page_size: paginationConfig.page_size.toString(), // Specify the number of items per page
      ordering: ordering, // Specify the field to sort by and the sorting order

      // TO DO remove testing
      // scanner_type: 'EC2',
      // source_type: 'endpoint_data',
      // ca_type: 'Server',
      // host_ids: '13',
      // age_min_exclusive: 1,
    };

    // source type filter
    // split the source type value at '__' and use the first part as the source type
    // which is the 2nd part as type of the ep and ca hosts (e.g. internal, external, servers, workstations etc.)
    if (sourceType) {
      const [sourceTypeValue, subType] = sourceType.split('__');
      params['source_type'] = sourceTypeValue;
      if (sourceTypeValue === 'endpoint_data' && subType) {
        params['scanner_type'] = subType;
      } else if (sourceTypeValue === 'ca_data' && subType) {
        params['ca_type'] = subType;
        if (agentKey != '') {
          params['ca_agent_key'] = agentKey;
        }
      }
    }

    // Add filter values to params
    // console.log('Filter values:', filterValues);
    Object.entries(filterValues).forEach(([key, value]) => {
      if (value && value.length > 0) {
        // special handling of pre defined filters
        if (key === 'predefinedFilters') {
          value.forEach((element) => {
            // console.log(`in state: ${element}`);
            const [paramKey, paramValue] = element.split('=');
            params[paramKey] = paramValue;
          });
        } else if (DateFilterKeys.includes(key)) {
          const fromDate = value[0]?.from;
          const toDate = value[0]?.to;
          // expected value format: { from: Date, to: Date }
          if (fromDate instanceof Date) {
            params[`${key}_after`] = fromDate.toISOString();
          } else {
            console.error({
              msg: 'fromDate is not a valid Date object:',
              fromDate,
            });
          }
          // if (fromDate) params[`${key}_after`] = fromDate?.toISOString();
          if (toDate) params[`${key}_before`] = toDate?.toISOString();
        } else if (key === 'host_ids') {
          // here value is an array of host information
          // so we need to extract the host ids
          const hostIds = value.map((host) => host.id);
          params[key] = hostIds.join(',');
        } else if (key == 'age') {
          const minValue = value[0]?.minValue;
          const maxValue = value[0]?.maxValue;
          const isMinExclusive = value[0]?.isMinExclusive;
          const isMaxExclusive = value[0]?.isMaxExclusive;
          if (minValue) {
            const paramKey = isMinExclusive ? 'age_min_exclusive' : 'age_min';
            params[paramKey] = minValue;
          }
          if (maxValue) {
            const paramKey = isMaxExclusive ? 'age_max_exclusive' : 'age_max';
            params[paramKey] = maxValue;
          }
        } else {
          params[key] = value.join(',');
        }
      }
    });

    return params;
  }
  return undefined;
});

export const findingsDataAtom = atom({
  results: [],
  count: 0,
  loading: false,
});

/**
 * reset the following atoms:
 * row selection
 * orderby
 * sort order
 * pagination
 */
export function useOnFilterChangeResetAtoms() {
  const resetSelectedRowIdsAtom = useResetAtom(selectedRowIdsAtom);
  const resetOrderbyAtom = useResetAtom(orderbyAtom);
  const resetSortOrderAtom = useResetAtom(sortOrderAtom);
  const resetPaginationConfigAtom = useResetAtom(paginationConfigAtom);

  const resetAtoms = () => {
    resetSelectedRowIdsAtom();
    resetOrderbyAtom();
    resetSortOrderAtom();
    resetPaginationConfigAtom();
  };

  return resetAtoms;
}
/**
 * reset the following atoms:
 * filter values
 * row selection
 * orderby
 * sort order
 * pagination
 */
export function useOnSourceTypeChangeFindingsResetAtoms() {
  const resetFilterValues = useResetAtom(filterValuesAtom);

  const resetOnFilterChangesResetFindingsAtoms = useOnFilterChangeResetAtoms();

  const resetAtoms = () => {
    resetOnFilterChangesResetFindingsAtoms();
    resetFilterValues();
  };

  return resetAtoms;
}

/**
 * reset the following atoms:
 * source type
 * filter values
 * row selection
 * orderby
 * sort order
 * pagination
 */
export function useOnCompanyChangeFindingsResetAtoms() {
  const resetSourceType = useResetAtom(sourceTypeAtom);

  const resetSourceTypeChangeFindingsResetAtoms =
    useOnSourceTypeChangeFindingsResetAtoms();

  const resetAtoms = () => {
    resetSourceTypeChangeFindingsResetAtoms();
    resetSourceType();
  };

  return resetAtoms;
}
